import React, { useState, useEffect } from 'react';
import axios from 'axios';

const PhotoUpload = ({ queryId, onUploadSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [existingPhoto, setExistingPhoto] = useState(null);

  // Check if query has existing photo on component mount
  useEffect(() => {
    console.log('PhotoUpload mounted with queryId:', queryId);
    if (queryId) {
      checkExistingPhoto();
    }
  }, [queryId]);

  const checkExistingPhoto = async () => {
    try {
      const response = await axios.get(`/api/queries/${queryId}/photo`);
      if (response.data && response.data.url) {
        setExistingPhoto(response.data.url);
      }
    } catch (error) {
      // No existing photo or error - that's okay
      setExistingPhoto(null);
    }
  };

  const compressImage = (file, maxWidth = 2000, maxHeight = 2000, quality = 0.85) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          // Calculate new dimensions while maintaining aspect ratio
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          
          // Create canvas and draw resized image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                // Create a new File object from the blob
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                console.log(`Compressed image: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
                resolve(compressedFile);
              } else {
                reject(new Error('Canvas to Blob conversion failed'));
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = () => reject(new Error('Image load failed'));
      };
      reader.onerror = () => reject(new Error('File read failed'));
    });
  };

  const handleFileSelect = async (event) => {
    console.log('handleFileSelect called', event.target.files);
    const file = event.target.files[0];
    console.log('Selected file:', file);
    console.log('Query ID:', queryId);
    
    if (!file || !queryId) {
      console.log('Missing file or queryId:', { file: !!file, queryId });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      console.log('Invalid file type:', file.type);
      setUploadStatus('Please select an image file');
      setTimeout(() => setUploadStatus(''), 3000);
      return;
    }

    console.log('Starting upload process...');
    setUploading(true);
    setUploadStatus('');

    try {
      let fileToUpload = file;
      
      // Compress image if it's larger than 1MB or dimensions are large
      if (file.size > 1 * 1024 * 1024) {
        console.log('Compressing image...');
        try {
          fileToUpload = await compressImage(file);
          
          // If still too large after compression, try with more aggressive settings
          if (fileToUpload.size > 5 * 1024 * 1024) {
            console.log('Still too large, applying more compression...');
            fileToUpload = await compressImage(file, 1600, 1600, 0.7);
          }
          
          // Final check - if still too large, reject
          if (fileToUpload.size > 5 * 1024 * 1024) {
            setUploadStatus('Image too large even after compression');
            setTimeout(() => setUploadStatus(''), 3000);
            setUploading(false);
            event.target.value = '';
            return;
          }
        } catch (compressionError) {
          console.error('Compression failed:', compressionError);
          setUploadStatus('Failed to compress image');
          setTimeout(() => setUploadStatus(''), 3000);
          setUploading(false);
          event.target.value = '';
          return;
        }
      }

      const formData = new FormData();
      formData.append('photo', fileToUpload);
      formData.append('query_id', queryId);

      console.log('Uploading photo for query:', queryId);
      console.log('FormData contents:', Array.from(formData.entries()));
      
      const response = await axios.post('/api/queries/upload_photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Upload response:', response.data);
      setUploadStatus('✓');
      setExistingPhoto(response.data.url);
      setTimeout(() => setUploadStatus(''), 2000);
      
      if (onUploadSuccess) {
        onUploadSuccess(response.data.url);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('✗');
      setTimeout(() => setUploadStatus(''), 2000);
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleButtonClick = () => {
    console.log('Button clicked, existingPhoto:', existingPhoto, 'queryId:', queryId);
    if (existingPhoto) {
      setShowModal(true);
    } else {
      const fileInput = document.getElementById(`photo-upload-${queryId}`);
      console.log('File input element:', fileInput);
      if (fileInput) {
        fileInput.click();
      } else {
        console.error('File input not found!');
      }
    }
  };

  const handleReplacePhoto = () => {
    setShowModal(false);
    document.getElementById(`photo-upload-${queryId}`).click();
  };

  const handleDeletePhoto = async () => {
    try {
      await axios.delete(`/api/queries/${queryId}/photo`);
      setExistingPhoto(null);
      setShowModal(false);
    } catch (error) {
      console.error('Delete photo error:', error);
    }
  };

  // Don't render if no queryId
  if (!queryId) {
    return (
      <div className="photo-upload-button-container">
        <button
          className="btn btn-outline-secondary btn-sm"
          disabled={true}
          title="Save query first to upload photo"
          style={{ width: '32px', height: '32px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <i className="fas fa-lock" style={{ fontSize: '10px' }}></i>
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="photo-upload-button-container">
        <input
          type="file"
          id={`photo-upload-${queryId}`}
          accept="image/*"
          onChange={handleFileSelect}
          disabled={uploading}
          style={{ display: 'none' }}
        />
        <button
          className={`btn btn-sm ${existingPhoto ? 'btn-success' : 'btn-outline-primary'}`}
          onClick={handleButtonClick}
          disabled={uploading}
          title={existingPhoto ? "View/Replace photo" : "Upload stock photo (images auto-compressed)"}
          style={{ width: '32px', height: '32px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {uploading ? (
            <span className="spinner-border spinner-border-sm" role="status" style={{ width: '12px', height: '12px' }}></span>
          ) : uploadStatus ? (
            <span style={{ fontSize: '12px' }}>{uploadStatus}</span>
          ) : (
            <i className="fas fa-image" style={{ fontSize: '12px', color: existingPhoto ? 'white' : 'inherit' }}></i>
          )}
        </button>
      </div>

      {/* Photo Modal */}
      {showModal && (
        <div className="modal show d-block modal-overlay" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Query Photo</h5>
                <button 
                  type="button" 
                  className="modal-close-button" 
                  onClick={() => setShowModal(false)}
                  aria-label="Close"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body text-center">
                {existingPhoto && (
                  <img 
                    src={existingPhoto} 
                    alt="Query photo" 
                    className="img-fluid mb-3"
                    style={{ maxHeight: '400px', borderRadius: '8px' }}
                  />
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={handleReplacePhoto}
                >
                  <i className="fas fa-upload me-2"></i>Replace Photo
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={handleDeletePhoto}
                >
                  <i className="fas fa-trash me-2"></i>Delete Photo
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PhotoUpload;
